import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { BackIcon, SearchIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { isUserImpersonated } from '../../../api/auth/is-user-impersonated'
import { reconnect } from '../../../api/auth/reconnect'
import { Paths } from '../../../lib/constants/path'
import { useSelectedEnrollment } from '../../../lib/contexts/selected-enrollment'
import useAuth from '../../../lib/hooks/use-auth'
import useSidebarManager from '../../../lib/hooks/use-sidebar-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { getUserDefaultRedirectionPath } from '../../pages/login/utils'
import SidebarItem from './sidebar-item'

type Props = {
  logo: React.ReactNode
  customButton?: React.ReactNode
}

const SidebarData = ({ logo, customButton }: Props) => {
  const [isReturningToAdmin, setIsReturningToAdmin] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { user, userRole } = useAuth()
  const { t } = useTranslate()
  const { navigate } = useNavigator()
  const { clearSelectedEnrollment, selectedEnrollmentId } = useSelectedEnrollment()
  const { search, items, isSidebarExpanded, handleSearchChange, handleSidebarExpansion } =
    useSidebarManager()

  const canCheckImpersonation = !!userRole

  const { data: userImpersonated } = useQuery({
    queryKey: ['isUserImpersonated', user?.id],
    queryFn: () => isUserImpersonated(),
    enabled: canCheckImpersonation
  })

  const reconnectMutation = useMutation({
    mutationFn: () => reconnect(),
    onSuccess: (data) => {
      setIsReturningToAdmin(true)
      let destination = getUserDefaultRedirectionPath(data)

      if (data.role === RoleEnum.ADMIN) {
        if (userRole === RoleEnum.CLIENT && selectedEnrollmentId)
          destination = Paths.ENROLLMENT_PROFILE_PAGE.replace(
            ':id',
            selectedEnrollmentId.toString()
          )
        else if (userRole === RoleEnum.Ol) destination = Paths.OL_LIST_PAGE
        else if (userRole === RoleEnum.STAFF) destination = Paths.STAFF_LIST_PAGE
      }

      window.location.assign(destination)
    }
  })

  useEffect(() => {
    if (userImpersonated) {
      if (search) handleSearchChange('')
      setIsReturningToAdmin(false)
    }
  }, [userImpersonated])

  return (
    <div className={cn('h-full px-5', { 'flex flex-col justify-between': !!customButton })}>
      <div className="bg-primary">
        <div className="flex justify-center">{logo}</div>
        <div className="h-9" />
        {userRole === RoleEnum.CLIENT && (
          <>
            <Button
              variant={'secondary'}
              className={'w-full'}
              label={isSidebarExpanded ? 'Retour à la liste des séjours' : undefined}
              icon={BackIcon}
              onClick={() => {
                void clearSelectedEnrollment()
                navigate(Paths.CLIENT_PERSONAL_SPACE_REDIRECTION)
              }}
            />
            <div className="h-2" />
          </>
        )}
        {userImpersonated && !isReturningToAdmin && (
          <Button
            variant={userRole === RoleEnum.CLIENT ? 'destructive' : 'highlighted'}
            className={'w-full mb-2'}
            label={isSidebarExpanded ? t('common.back.to.admin.space') : undefined}
            icon={BackIcon}
            loading={reconnectMutation.isPending}
            onClick={async () => reconnectMutation.mutateAsync()}
          />
        )}
        {userRole !== RoleEnum.CLIENT ? (
          isSidebarExpanded ? (
            <Input
              type="text"
              options={{ clear: true }}
              icon={SearchIcon}
              wrapperClassName="border-primary focus-within:border-secondary bg-white"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          ) : (
            <div className="px-[2px] duration-300">
              <div
                onClick={() => handleSidebarExpansion(true)}
                className="flex pl-[12px] items-center bg-white h-10 w-full rounded duration-300 cursor-pointer"
              >
                <SearchIcon color={tailwindColors.grey.lighter} width="20px" height="20px" />
              </div>
            </div>
          )
        ) : (
          <></>
        )}
        <div className="h-5" />
      </div>
      <div
        ref={ref}
        className={cn(
          'h-full space-y-3 pr-1 overflow-y-auto flex-1 overflow-x-hidden max-h-[calc(100vh-271px)] hover:sidebar-scrollbar',
          { 'pb-14': userImpersonated }
        )}
      >
        {items.map((item) => (
          <SidebarItem key={item.itemKey} {...item} variant="thick-padding" />
        ))}
      </div>
      {customButton && (
        <div className="pt-3 sticky bottom-0 z-10 flex-shrink-0">{customButton}</div>
      )}
    </div>
  )
}

export default SidebarData
